import { useCanCurrentUserAccessWebAdminSettings } from '@ms/yammer-hooks/dist/adminSettings';
import { useLoadFeatureAccessInsights } from '@ms/yammer-hooks/dist/featureAccessInsights';
import { useLoadMultiTenantOrganizationDetails } from '@ms/yammer-hooks/dist/multiTenantOrganizationDetails/useLoadMultiTenantOrganizationDetails';
import { usePushSubscription } from '@ms/yammer-hooks/dist/pushSubscriptions';
import { useServiceWorkerMessages } from '@ms/yammer-hooks/dist/pwa';
import { useLoadStorylineSettings } from '@ms/yammer-hooks/dist/storylineSettings';
import { useSendPresenceState } from '@ms/yammer-hooks/dist/userPresence';
import { useCurrentLocaleDirection } from '@ms/yammer-i18n-react';
import { usePersistLocale } from '@ms/yammer-i18n-react/dist/hooks';
import { ReduxAnnouncerBridge } from '@ms/yammer-web-components/dist/accessibility/Announcer';
import Skiplink from '@ms/yammer-web-components/dist/accessibility/Skiplink';
import AppContextProvider from '@ms/yammer-web-components/dist/app/AppContext';
import AppDocumentHeadOverrides from '@ms/yammer-web-components/dist/app/AppDocumentHeadOverrides';
import MediaLightbox from '@ms/yammer-web-components/dist/messageAttachments/MediaLightbox';
import RealtimeClient from '@ms/yammer-web-components/dist/realtime/RealtimeClient';
import SessionContextProviderWithHooks from '@ms/yammer-web-components/dist/session/SessionContextProviderWithHooks';
import AppContainer from '@ms/yammer-web-core-ui/dist/components/AppContainer';
import { PageLayoutContentMainAreaAriaRole } from '@ms/yammer-web-core-ui/dist/components/PageLayout';
import { usePrepareIcons } from '@ms/yammer-web-core-ui/dist/util/icons';
import { useMainFeatureSettings } from '@ms/yammer-web-ui-features/dist/featureSettings';
import { UIFeaturesProvider } from '@ms/yammer-web-ui-features/dist/features';
import React, { FC } from 'react';

import Dialogs from '../Dialogs';
import Routes from '../Routes';

import { useThemeWithSuiteHeaderOverrides } from './hooks';

const App: FC = () => {
  usePersistLocale();
  useServiceWorkerMessages();
  usePushSubscription();
  useSendPresenceState();
  usePrepareIcons();
  useLoadStorylineSettings();
  useLoadMultiTenantOrganizationDetails();
  useLoadFeatureAccessInsights({ vivaEngagePremiumOnly: true });

  const features = useMainFeatureSettings({
    overrides: {
      navigation: {
        admin: useCanCurrentUserAccessWebAdminSettings(),
      },
    },
  });
  const theme = useThemeWithSuiteHeaderOverrides();
  const dir = useCurrentLocaleDirection();

  return (
    <AppContainer theme={theme} dir={dir}>
      <UIFeaturesProvider features={features}>
        <SessionContextProviderWithHooks>
          <AppContextProvider mainAreaAriaRole={PageLayoutContentMainAreaAriaRole.Main}>
            <AppDocumentHeadOverrides />
            <Skiplink />
            <Routes />
            <Dialogs />
            <MediaLightbox />
            <RealtimeClient />
            <ReduxAnnouncerBridge />
          </AppContextProvider>
        </SessionContextProviderWithHooks>
      </UIFeaturesProvider>
    </AppContainer>
  );
};
export default App;
